import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { getEmpresaAtual } from './api';

// ============================================================
// Relatório de Propostas Reprovadas por CPF/CNPJ — Marcki v1
// ============================================================

const generatePdfRejeitadosCpfCnpj = async (data, filtro, isSupervisor) => {
  if (!data) return;

  const pdfDoc  = await PDFDocument.create();
  const fontReg = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold= await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  const W = 595.28; const H = 841.89;
  const ML = 40; const MR = 40;
  const CW = W - ML - MR;

  const C = {
    cinzaChumbo: rgb(0.251, 0.251, 0.251),
    cinzaMedio:  rgb(0.450, 0.450, 0.450),
    cinzaClaro:  rgb(0.941, 0.941, 0.941),
    laranja:     rgb(0.878, 0.431, 0.000),
    laranjaClaro:rgb(0.996, 0.937, 0.878),
    laranjaEsc:  rgb(0.650, 0.310, 0.000),
    branco:      rgb(1, 1, 1),
    preto:       rgb(0.133, 0.133, 0.133),
    borda:       rgb(0.780, 0.780, 0.780),
    aviso:       rgb(0.600, 0.400, 0.000),
    avisoBg:     rgb(1.000, 0.970, 0.880),
  };

  const brl = (v) => parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const drawRect  = (pg, x, y, w, h, color) => pg.drawRectangle({ x, y, width: w, height: h, color });
  const drawBRect = (pg, x, y, w, h, fill, border) => pg.drawRectangle({ x, y, width: w, height: h, color: fill, borderColor: border || C.borda, borderWidth: 0.5 });
  const drawText  = (pg, t, x, y, s, f, color) => pg.drawText(String(t ?? ''), { x, y, size: s, font: f, color });
  const drawLine  = (pg, x1, y1, x2, y2, color = C.borda, t = 0.5) => pg.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: t, color });

  const tH = 14;
  const drawRow = (pg, cells, y) => {
    cells.forEach((c) => {
      drawBRect(pg, c.x, y - tH + 2, c.w, tH, c.fill, c.bc || C.borda);
      if (c.text != null && c.text !== '') {
        const sz = c.sz || 8; const fn = c.fn || fontReg;
        let tx = c.x + 3;
        if (c.al === 'r') tx = c.x + c.w - fn.widthOfTextAtSize(String(c.text), sz) - 3;
        if (c.al === 'c') tx = c.x + (c.w - fn.widthOfTextAtSize(String(c.text), sz)) / 2;
        drawText(pg, c.text, tx, y - tH + 5, sz, fn, c.cl || C.preto);
      }
    });
    return y - tH;
  };

  // ── Logo ──────────────────────────────────────────────────────────────────
  let logoImage = null;
  try {
    const empresa = await getEmpresaAtual();
    if (empresa?.logoBranca) {
      const base64 = empresa.logoBranca.split(',')[1];
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      logoImage = await pdfDoc.embedPng(bytes);
    }
  } catch (e) { /* sem logo */ }

  // ── Cabeçalho ─────────────────────────────────────────────────────────────
  const drawHeader = (pg) => {
    drawRect(pg, 0, H - 44, W, 44, C.cinzaChumbo);
    drawRect(pg, 0, H - 44, 6, 44, C.laranja);
    if (logoImage) {
      const lDims = logoImage.scaleToFit(70, 28);
      pg.drawImage(logoImage, { x: ML, y: H - 38, width: lDims.width, height: lDims.height });
    } else {
      drawText(pg, 'Marcki Engenharia', ML, H - 26, 11, fontBold, C.branco);
    }
    const titulo = 'Relatório de Propostas Reprovadas por CPF/CNPJ';
    drawText(pg, titulo, W - MR - fontBold.widthOfTextAtSize(titulo, 9), H - 24, 9, fontBold, C.branco);
    const periodo = [
      filtro?.datainicial ? new Date(filtro.datainicial).toLocaleDateString('pt-BR') : '',
      filtro?.datafinal   ? new Date(filtro.datafinal).toLocaleDateString('pt-BR')   : '',
    ].filter(Boolean).join(' — ');
    if (periodo) drawText(pg, periodo, W - MR - fontReg.widthOfTextAtSize(periodo, 8), H - 36, 8, fontReg, rgb(0.8, 0.8, 0.8));
    return H - 58;
  };

  // ── Rodapé ────────────────────────────────────────────────────────────────
  const drawFooter = (pg) => {
    drawRect(pg, 0, 0, W, 26, C.cinzaChumbo);
    drawRect(pg, 0, 0, 6, 26, C.laranja);
    drawText(pg, 'Marcki Engenharia — Araguari/MG', ML + 8, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
    drawText(pg, `Gerado em ${new Date().toLocaleDateString('pt-BR')}`, W - MR - 70, 9, 7, fontReg, rgb(0.8, 0.8, 0.8));
    if (!isSupervisor) drawText(pg, 'Dados pessoais protegidos por LGPD', W / 2 - 60, 9, 7, fontReg, rgb(0.7, 0.5, 0.0));
  };

  // ── Mascaramento LGPD ─────────────────────────────────────────────────────
  const mascCpf = (v) => {
    if (!v) return '—';
    const s = v.replace(/\D/g, '');
    return s.length === 11 ? `***.${s.slice(3, 6)}.${s.slice(6, 9)}-**` : '***';
  };
  const mascCnpj = (v) => {
    if (!v) return '—';
    const s = v.replace(/\D/g, '');
    return s.length === 14 ? `**.${s.slice(2, 5)}.${s.slice(5, 8)}/***/***-**` : '***';
  };
  const mascTel = (v) => {
    if (!v) return '—';
    const s = v.replace(/\D/g, '');
    return s.length >= 10 ? `(${s.slice(0, 2)}) ${s.slice(2, 7)}-**` : '***';
  };

  const exibirDoc = (p) => {
    const isCpf = (p.motivoNome || '').toLowerCase().includes('cpf');
    const val   = isCpf ? p.cliente?.cpf : p.cliente?.cnpj;
    if (isSupervisor) return val || '—';
    return isCpf ? mascCpf(val) : mascCnpj(val);
  };
  const exibirData = (p) => {
    const isCpf = (p.motivoNome || '').toLowerCase().includes('cpf');
    const val   = isCpf ? p.cliente?.nascimento : p.cliente?.datafundacao;
    if (!val) return '—';
    if (isSupervisor) return new Date(val).toLocaleDateString('pt-BR');
    return '**/**/****';
  };
  const exibirTel = (p) => {
    const val = p.cliente?.telefone;
    if (isSupervisor) return val || '—';
    return mascTel(val);
  };

  // ── Filtrar linhas ────────────────────────────────────────────────────────
  const linhas = (data.linhas || []).filter((p) => {
    const m = (p.motivoNome || '').toLowerCase();
    return m.includes('cpf reprovado') || m.includes('cnpj reprovado');
  });

  const totalCpf  = linhas.filter((p) => (p.motivoNome || '').toLowerCase().includes('cpf')).length;
  const totalCnpj = linhas.filter((p) => (p.motivoNome || '').toLowerCase().includes('cnpj')).length;
  const totalVal  = linhas.reduce((s, p) => s + (p.valor || 0), 0);

  // ============================================================
  // PG 1
  // ============================================================
  const p1 = pdfDoc.addPage([W, H]);
  let y1 = drawHeader(p1);
  y1 -= 8;

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpiW = (CW - 15) / 4;
  const kpiH = 36;
  const kpis = [
    { label: 'CPF Reprovados',  valor: String(totalCpf) },
    { label: 'CNPJ Reprovados', valor: String(totalCnpj) },
    { label: 'Total',           valor: String(linhas.length) },
    { label: 'Valor Total',     valor: isSupervisor ? brl(totalVal) : 'Restrito' },
  ];

  kpis.forEach((kpi, i) => {
    const x = ML + i * (kpiW + 5);
    drawBRect(p1, x, y1 - kpiH, kpiW, kpiH, C.laranjaClaro, C.laranja);
    drawRect(p1, x, y1 - kpiH, 3, kpiH, C.laranja);
    drawText(p1, kpi.label, x + 6, y1 - 13, 7, fontReg, C.cinzaMedio);
    drawText(p1, kpi.valor, x + 6, y1 - 28, 11, fontBold, C.cinzaChumbo);
  });
  y1 -= kpiH + 12;

  // ── Aviso LGPD ────────────────────────────────────────────────────────────
  if (!isSupervisor) {
    drawBRect(p1, ML, y1 - 16, CW, 16, C.avisoBg, C.aviso);
    drawText(p1, 'Dados sensíveis (CPF/CNPJ, telefone, datas) exibidos de forma parcial conforme LGPD.', ML + 4, y1 - 11, 7, fontReg, C.aviso);
    y1 -= 24;
  }

  // ── Tabela ────────────────────────────────────────────────────────────────
  drawRect(p1, ML, y1 + 2, CW, 16, C.cinzaChumbo);
  drawText(p1, 'PROPOSTAS REPROVADAS POR CPF/CNPJ', ML + 6, y1 + 7, 8, fontBold, C.branco);
  y1 -= 16;

  const col1 = CW * 0.19; const col2 = CW * 0.17; const col3 = CW * 0.13;
  const col4 = CW * 0.14; const col5 = CW * 0.14; const col6 = CW * 0.12;
  const col7 = CW - col1 - col2 - col3 - col4 - col5 - col6;

  y1 = drawRow(p1, [
    { x: ML,                              w: col1, fill: C.laranja, text: 'Cliente',                                          fn: fontBold, cl: C.branco },
    { x: ML+col1,                         w: col2, fill: C.laranja, text: isSupervisor ? 'CPF/CNPJ' : 'CPF/CNPJ (mascarado)', fn: fontBold, cl: C.branco },
    { x: ML+col1+col2,                    w: col3, fill: C.laranja, text: isSupervisor ? 'Nasc./Fund.' : 'Nasc. (mascarado)', fn: fontBold, cl: C.branco },
    { x: ML+col1+col2+col3,               w: col4, fill: C.laranja, text: isSupervisor ? 'Telefone' : 'Telefone (masc.)',     fn: fontBold, cl: C.branco },
    { x: ML+col1+col2+col3+col4,          w: col5, fill: C.laranja, text: 'Valor',                                           fn: fontBold, cl: C.branco, al: 'r' },
    { x: ML+col1+col2+col3+col4+col5,     w: col6, fill: C.laranja, text: 'Consultor',                                       fn: fontBold, cl: C.branco },
    { x: ML+col1+col2+col3+col4+col5+col6, w: col7, fill: C.laranja, text: 'Motivo',                                          fn: fontBold, cl: C.branco },
  ], y1);

  if (linhas.length === 0) {
    drawText(p1, 'Nenhuma proposta reprovada por CPF/CNPJ no período selecionado.', ML + 4, y1 - 20, 8, fontReg, C.cinzaMedio);
  } else {
    linhas.forEach((p, i) => {
      if (y1 < 50) return;
      const fill = i % 2 === 0 ? C.cinzaClaro : C.branco;
      y1 = drawRow(p1, [
        { x: ML,                              w: col1, fill, text: (p.cliente?.nome || '').substring(0, 20) },
        { x: ML+col1,                         w: col2, fill, text: exibirDoc(p).substring(0, 20) },
        { x: ML+col1+col2,                    w: col3, fill, text: exibirData(p) },
        { x: ML+col1+col2+col3,               w: col4, fill, text: exibirTel(p) },
        { x: ML+col1+col2+col3+col4,          w: col5, fill, text: isSupervisor ? brl(p.valor) : 'Rest.', al: 'r' },
        { x: ML+col1+col2+col3+col4+col5,     w: col6, fill, text: (p.user?.nome || '').substring(0, 13) },
        { x: ML+col1+col2+col3+col4+col5+col6, w: col7, fill, text: (p.motivoNome || '').substring(0, 14) },
      ], y1);
    });

    y1 -= 6;
    drawBRect(p1, ML, y1 - tH + 2, CW, tH, C.laranjaClaro, C.laranja);
    drawText(p1, `Total: ${linhas.length} | CPF: ${totalCpf} | CNPJ: ${totalCnpj}`, ML + 4, y1 - tH + 5, 8, fontBold, C.laranjaEsc);
    if (isSupervisor) drawText(p1, `Valor Total: ${brl(totalVal)}`, ML + col1 + col2 + col3, y1 - tH + 5, 8, fontBold, C.laranjaEsc);
  }

  drawFooter(p1);

  const pdfBytes = await pdfDoc.save();
  const blob     = new Blob([pdfBytes], { type: 'application/pdf' });
  const url      = URL.createObjectURL(blob);
  const link     = document.createElement('a');
  link.href      = url;
  link.download  = 'relatorio-cpf-cnpj-reprovados.pdf';
  link.click();
  URL.revokeObjectURL(url);
};

export default generatePdfRejeitadosCpfCnpj;